import { format, isValid } from 'date-fns'
import { de } from 'date-fns/locale'
import type { ReactElement } from 'react'
import { appColors } from '../theme/foundations/colors'

interface GraphTickProps {
  payload: { value: any; index: number }
  x: number | string
  y: number | string
  showYear?: boolean
  rotate?: boolean | 'clockwise' | 'counterclockwise'
  fontSize?: string
  transformX?: (x: number, index?: number) => number
  transformY?: (y: number, index?: number) => number
  transformXYear?: (x: number, index?: number) => number
  formatter?: (value: any, index: number) => string
}

/**
 * @obsolete Do not use this component. Use GraphTick2 instead in combination of useXAxisProps hook.
 */
export const GraphTick = ({
  payload,
  x,
  y,
  rotate = false,
  showYear = false,
  fontSize = '12px',
  transformX,
  transformY,
  transformXYear,
  formatter,
}: GraphTickProps): ReactElement<SVGElement> => {
  const { value, index } = payload
  if (value === undefined || value === null) return <g></g>

  const formattedValue = formatter ? formatter(value, index) : value

  // Default positions
  const positionX = transformX ? transformX(Number(x), index) : Number(x)
  const positionY = transformY ? transformY(Number(y), index) : rotate ? Number(y) + 10 : Number(y)
  const rotation = rotate === true || rotate === 'clockwise' ? 'rotate(45 0 0)' : rotate === 'counterclockwise' ? 'rotate(-45 0 0)' : ''

  const renderTick = () => (
    <g transform={`translate(${positionX},${positionY}) ${rotation}`}>
      <text textAnchor='middle' fill={appColors.gray['400']}>
        <tspan key={`graph-tick-${index}`} x={0} fontSize={fontSize} letterSpacing={'0.24px'}>
          {formattedValue}
        </tspan>
      </text>
    </g>
  )

  const renderYear = () => {
    const date = value instanceof Date ? value : new Date(value)

    // if invalid date, render nothing
    if (!isValid(date)) return <g></g>

    return (
      <g transform={`translate(${transformXYear ? transformXYear(Number(x), index) : positionX},${positionY + 15})`}>
        <text textAnchor='middle' fill={appColors.gray['400']}>
          <tspan key={`graph-tick-year-${index}`} x={0} fontSize={fontSize} letterSpacing={'0.24px'}>
            {format(date, 'yyyy', { locale: de })}
          </tspan>
        </text>
      </g>
    )
  }

  return (
    <g>
      {renderTick()}
      {showYear && renderYear()}
    </g>
  )
}
